import { useCallback, useEffect, useRef, useState } from 'react';

type LookupResponse<T> = {
  data: T[];
  isSuccess: boolean;
};

type UseLookupOptions = {
  enabled?: boolean;
};

const useLookup = <T>(loader: () => Promise<LookupResponse<T>>, options: UseLookupOptions = {}) => {
  const { enabled = true } = options;
  const requestIdRef = useRef(0);
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async (): Promise<boolean> => {
    if (!enabled) return false;

    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      const response = await loader();

      if (requestId !== requestIdRef.current || !response.isSuccess) return false;

      setItems(response.data);
      setLoaded(true);

      return true;
    } catch {
      // HTTP errors are surfaced by the shared response interceptor.
      return false;
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, loader]);

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current += 1;
      setLoading(false);
      setLoaded(false);
      return;
    }

    setLoaded(false);
    void refresh();

    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, refresh]);

  return { items, loading, loaded, refresh };
};

export default useLookup;
